import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from 'react'
import logger from 'shared/services/logger'
import { isUserError } from 'shared/utils/errors'
import { getClientSession } from '../../utils/auth/v2/client-session'
import {
  isSessionValid,
  isSessionCloseToExpiring,
  hasCompleteUserData,
  mergeSessionData,
  toSessionData,
} from './utils'
import type { ClientSession } from './types'

interface SessionProviderProps {
  session?: ClientSession | null
  children: ReactNode
}

interface SessionContext {
  session: ClientSession | null
  active: boolean
  loading: boolean
  error: boolean
  update(): Promise<void>
}

export const SessionContext = createContext<SessionContext | null>(null)

export const SessionProvider = ({
  children,
  session: defaultSession,
}: SessionProviderProps) => {
  const [session, setSession] = useState<ClientSession | null>(
    toSessionData(defaultSession),
  )
  const sessionRef = useRef<ClientSession | null>(toSessionData(defaultSession))
  const [isActive, setIsActive] = useState<boolean>(Boolean(defaultSession))
  const [isFailed, setIsFailed] = useState<boolean>(false)
  const updateInProgressRef = useRef<boolean>(false)

  useEffect(() => {
    sessionRef.current = session
  }, [session])

  const shouldLoadSession = useMemo(() => {
    return (
      !isActive &&
      !isFailed &&
      (defaultSession === null || defaultSession === undefined)
    )
  }, [isActive, isFailed, defaultSession])

  const updateSession = useCallback(async () => {
    if (isFailed || updateInProgressRef.current) return

    try {
      updateInProgressRef.current = true
      setIsFailed(false)

      const fetchedSession = await getClientSession()
      const newSession = toSessionData(fetchedSession)

      if (!newSession) {
        setSession(null)
        setIsFailed(true)
        updateInProgressRef.current = false
        return
      }

      if (!isSessionValid(newSession)) {
        if (!sessionRef.current || !isSessionValid(sessionRef.current)) {
          setIsFailed(true)
          setSession(null)
          updateInProgressRef.current = false
          return
        }

        updateInProgressRef.current = false
        return
      }

      if (!hasCompleteUserData(newSession)) {
        if (sessionRef.current && hasCompleteUserData(sessionRef.current)) {
          setSession(mergeSessionData(sessionRef.current, newSession))
          updateInProgressRef.current = false
          return
        }

        logger.error(
          new Error(
            'NewSessionProvider: Incomplete session and no valid existing session to merge',
          ),
          {
            extra: {
              hasExistingSession: !!sessionRef.current,
              existingComplete: hasCompleteUserData(sessionRef.current),
            },
          },
        )
        setSession(newSession)
        updateInProgressRef.current = false
        return
      }

      setSession(
        sessionRef.current
          ? mergeSessionData(sessionRef.current, newSession)
          : newSession,
      )
    } catch (error) {
      if (isUserError(error)) {
        setSession(null)
        setIsFailed(true)
        updateInProgressRef.current = false
        return
      }

      setIsFailed(false)
    } finally {
      updateInProgressRef.current = false
      setIsActive(true)
    }
  }, [isFailed])

  useEffect(() => {
    if (shouldLoadSession) updateSession()
  }, [shouldLoadSession, updateSession])

  useEffect(() => {
    if (!session) return

    const expirationTime = Math.max(session.expiration - Date.now(), 0)
    const checkSessionExpiration = setTimeout(() => {
      void updateSession()
    }, expirationTime)

    return () => clearTimeout(checkSessionExpiration)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.expiration])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && session) {
        if (session.expiration - Date.now() <= 30000) {
          void updateSession()
        }
      }
    }

    const handleFocus = () => {
      if (session && session.expiration - Date.now() <= 60000) {
        void updateSession()
      }
    }

    const handleOnline = () => {
      if (session && isSessionCloseToExpiring(session, 300000)) {
        void updateSession()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    if (process.env.NODE_ENV !== 'test') {
      window.addEventListener('online', handleOnline)
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      if (process.env.NODE_ENV !== 'test') {
        window.removeEventListener('online', handleOnline)
      }
    }
  }, [session, updateSession])

  useEffect(() => {
    if (!session || process.env.NODE_ENV === 'test') return

    const healthCheckInterval = setInterval(() => {
      if (!isSessionValid(session)) {
        void updateSession()
      }
    }, 60000)

    return () => clearInterval(healthCheckInterval)
  }, [session, updateSession])

  const value = useMemo(() => {
    return {
      session,
      error: isFailed,
      active: isActive,
      loading: updateInProgressRef.current,
      update: updateSession,
    }
  }, [session, isFailed, isActive, updateSession])

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  )
}

export function useSession(): SessionContext {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }
  return context
}
